import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { paymentsOnlineApi } from '../../lib/api';
import { ChartLineUp, Wallet } from '@phosphor-icons/react';

const CHANNEL_LABEL = {
  wave: 'Wave',
  orange: 'Orange Money',
  mtn: 'MTN MoMo',
  moov: 'Moov Money',
  card: 'Carte bancaire',
  unknown: 'Autre / Inconnu',
};

const CHANNEL_COLOR = {
  wave: '#00D0FF',
  orange: '#FF7900',
  mtn: '#FFCC00',
  moov: '#005EB8',
  card: '#7C3AED',
  unknown: '#94A3B8',
};

const CATEGORY_LABEL = {
  tuition: 'Scolarité',
  canteen: 'Cantine',
  transport: 'Transport',
  registration: 'Inscription',
  reregistration: 'Réinscription',
};

const fmtFcfa = (n) => `${Number(n || 0).toLocaleString('fr-FR')} FCFA`;

const legendFormatter = (value) => (
  <span style={{ fontSize: 11, color: '#475569' }}>{value}</span>
);

export default function GeniuspayAnalytics() {
  const [range, setRange] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    paymentsOnlineApi
      .geniuspayAnalytics(range)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const daily = useMemo(() => (data?.daily || []).map((d) => ({
    date: (d.date || '').slice(5),
    amount: d.amount,
    count: d.count,
  })), [data]);

  const channels = useMemo(() => (data?.channels || []).map((c) => ({
    name: CHANNEL_LABEL[c.channel] || c.channel,
    value: c.amount,
    key: c.channel,
    count: c.count,
  })), [data]);

  const hasData = (data?.totals?.count || 0) > 0;

  return (
    <Card
      className="border border-[#E2E8F0] shadow-none"
      data-testid="geniuspay-analytics-card"
    >
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="font-outfit text-base flex items-center gap-2">
            <div className="p-1.5 rounded-md bg-indigo-50">
              <ChartLineUp size={18} className="text-indigo-600" weight="duotone" />
            </div>
            Analytics GeniusPay
          </CardTitle>
          <div className="flex items-center gap-2 text-xs">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setRange(d)}
                data-testid={`geniuspay-analytics-range-${d}`}
                className={`px-2.5 py-1 rounded-md border transition-colors ${
                  range === d
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-[#475569] border-[#E2E8F0] hover:bg-indigo-50'
                }`}
              >
                {d}j
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-4">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-indigo-50/50 border border-indigo-100">
            <p className="text-xs text-[#64748B]">Volume encaissé</p>
            <p className="font-outfit text-lg font-semibold text-indigo-700" data-testid="geniuspay-analytics-total">
              {fmtFcfa(data?.totals?.amount)}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-50/50 border border-emerald-100">
            <p className="text-xs text-[#64748B]">Paiements réussis</p>
            <p className="font-outfit text-lg font-semibold text-emerald-700">
              {data?.totals?.count ?? 0}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-sky-50/50 border border-sky-100">
            <p className="text-xs text-[#64748B]">Ticket moyen</p>
            <p className="font-outfit text-lg font-semibold text-sky-700">
              {fmtFcfa(data?.totals?.avg)}
            </p>
          </div>
          <div className="p-3 rounded-lg bg-amber-50/50 border border-amber-100">
            <p className="text-xs text-[#64748B]">En attente</p>
            <p className="font-outfit text-lg font-semibold text-amber-700">
              {data?.pending_count ?? 0}
            </p>
          </div>
        </div>

        {loading && (
          <p className="text-xs text-[#64748B] text-center py-8">Chargement…</p>
        )}

        {!loading && !hasData && (
          <div className="text-center py-8 border border-dashed border-[#E2E8F0] rounded-lg">
            <Wallet size={32} className="mx-auto text-[#94A3B8] mb-2" weight="duotone" />
            <p className="text-sm font-outfit text-[#0F172A]">Aucun paiement confirmé sur cette période</p>
            <p className="text-xs text-[#64748B] mt-1">
              Les paiements Wave / Orange / MTN / Cartes apparaîtront ici dès qu&apos;un parent aura payé via GeniusPay.
            </p>
          </div>
        )}

        {!loading && hasData && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Daily bar chart (2 cols) */}
            <div className="lg:col-span-2">
              <p className="text-xs font-semibold text-[#475569] mb-2">Encaissements jour par jour</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={daily} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#64748B' }} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                    <Tooltip
                      formatter={(value, name) => name === 'amount' ? [fmtFcfa(value), 'Montant'] : [value, 'Paiements']}
                      labelStyle={{ fontFamily: 'Outfit' }}
                    />
                    <Bar dataKey="amount" fill="#4F46E5" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Channel pie */}
            <div>
              <p className="text-xs font-semibold text-[#475569] mb-2">Par méthode de paiement</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={channels}
                      dataKey="value"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                    >
                      {channels.map((c) => (
                        <Cell
                          key={c.key}
                          fill={CHANNEL_COLOR[c.key] || '#64748B'}
                          data-testid={`geniuspay-channel-${c.key}`}
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(value, name, item) => [
                        `${fmtFcfa(value)} (${item?.payload?.count || 0} pmts)`,
                        item?.payload?.name,
                      ]}
                    />
                    <Legend
                      verticalAlign="bottom"
                      height={20}
                      formatter={legendFormatter}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Category breakdown */}
        {!loading && hasData && (data?.categories || []).length > 0 && (
          <div className="border-t border-[#E2E8F0] pt-3">
            <p className="text-xs font-semibold text-[#475569] mb-2">Top catégories</p>
            <div className="space-y-1.5">
              {(data.categories || []).map((c) => {
                const pct = data.totals.amount > 0 ? (c.amount / data.totals.amount) * 100 : 0;
                return (
                  <div key={c.category} className="flex items-center gap-3 text-xs" data-testid={`geniuspay-cat-${c.category}`}>
                    <span className="w-24 text-[#0F172A] font-medium">
                      {CATEGORY_LABEL[c.category] || c.category}
                    </span>
                    <div className="flex-1 h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500"
                        style={{ width: `${Math.min(100, pct).toFixed(1)}%` }}
                      />
                    </div>
                    <span className="w-32 text-right text-[#475569] font-mono">
                      {fmtFcfa(c.amount)}
                    </span>
                    <span className="w-10 text-right text-[#94A3B8]">
                      {c.count}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
